import { ReactNode, SVGProps } from 'react';

type IconProps = Omit<SVGProps<SVGSVGElement>, 'stroke' | 'strokeWidth' | 'fill' | 'width' | 'height'> & {
  size?: number;
  strokeWidth?: number;
};

const baseProps = (size: number, stroke: number) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: stroke,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  style: { flex: 'none' as const },
});

function Stroke({ d, size = 16, strokeWidth = 1.5, ...rest }: IconProps & { d: string }) {
  return (
    <svg {...baseProps(size, strokeWidth)} {...rest}>
      <path d={d} />
    </svg>
  );
}

function Custom({ size = 16, strokeWidth = 1.5, children, ...rest }: IconProps & { children: ReactNode }) {
  return (
    <svg {...baseProps(size, strokeWidth)} {...rest}>
      {children}
    </svg>
  );
}

export const IconHome = (p: IconProps) => (
  <Stroke d="M3 11.5 12 4l9 7.5V20a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z" {...p} />
);
export const IconFolder = (p: IconProps) => (
  <Stroke d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" {...p} />
);
export const IconSpark = (p: IconProps) => (
  <Stroke
    d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8"
    {...p}
  />
);
export const IconPlay = ({ size = 16, ...rest }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ flex: 'none' }} {...rest}>
    <path d="M8 5.5v13a.5.5 0 0 0 .77.42l10-6.5a.5.5 0 0 0 0-.84l-10-6.5A.5.5 0 0 0 8 5.5Z" />
  </svg>
);
export const IconPackage = (p: IconProps) => (
  <Stroke d="M3 7.5 12 3l9 4.5v9L12 21l-9-4.5zM3 7.5 12 12m0 0 9-4.5M12 12v9" {...p} />
);
export const IconChat = (p: IconProps) => (
  <Stroke d="M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H9l-4 4v-4H6a2 2 0 0 1-2-2z" {...p} />
);
export const IconBook = (p: IconProps) => (
  <Stroke d="M4 5a2 2 0 0 1 2-2h12v16H6a2 2 0 0 0-2 2zM4 19a2 2 0 0 1 2-2h12" {...p} />
);
export const IconCredit = (p: IconProps) => (
  <Stroke d="M3 7h18M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7M3 7l2-3h14l2 3M7 14h4" {...p} />
);
export const IconCog = (p: IconProps) => (
  <Custom {...p}>
    <circle cx="12" cy="12" r="2.5" />
    <path d="M19.4 13a7.5 7.5 0 0 0 0-2l2-1.5-2-3.4-2.4.9a7.5 7.5 0 0 0-1.7-1L15 3h-4l-.3 2.5a7.5 7.5 0 0 0-1.7 1l-2.4-.9-2 3.4L4.6 11a7.5 7.5 0 0 0 0 2l-2 1.5 2 3.4 2.4-.9a7.5 7.5 0 0 0 1.7 1L9 21h6l.3-2.5a7.5 7.5 0 0 0 1.7-1l2.4.9 2-3.4z" />
  </Custom>
);
export const IconSearch = (p: IconProps) => <Stroke d="M11 4a7 7 0 1 1 0 14 7 7 0 0 1 0-14ZM21 21l-5-5" {...p} />;
export const IconPlus = (p: IconProps) => <Stroke d="M12 5v14M5 12h14" strokeWidth={2} {...p} />;
export const IconArrow = (p: IconProps) => <Stroke d="M5 12h14M13 6l6 6-6 6" {...p} />;
export const IconArrowL = (p: IconProps) => <Stroke d="M19 12H5M11 6l-6 6 6 6" {...p} />;
export const IconChevronL = (p: IconProps) => <Stroke d="M15 18l-6-6 6-6" {...p} />;
export const IconChevronR = (p: IconProps) => <Stroke d="M9 18l6-6-6-6" {...p} />;
export const IconChevronD = (p: IconProps) => <Stroke d="M6 9l6 6 6-6" {...p} />;
export const IconCheck = (p: IconProps) => <Stroke d="M4 12.5 9.5 18 20 6.5" strokeWidth={2} {...p} />;
export const IconX = (p: IconProps) => <Stroke d="M6 6l12 12M6 18 18 6" {...p} />;
export const IconMenu = (p: IconProps) => <Stroke d="M4 7h16M4 12h16M4 17h16" {...p} />;
export const IconSend = (p: IconProps) => <Stroke d="M4 12 21 4l-4 16-5-7zM4 12l8 1" {...p} />;
export const IconGlobe = (p: IconProps) => (
  <Stroke
    d="M3 12a9 9 0 1 0 18 0 9 9 0 0 0-18 0ZM3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18"
    {...p}
  />
);
export const IconDesktop = (p: IconProps) => (
  <Stroke d="M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM9 21h6M12 17v4" {...p} />
);
export const IconPhone = (p: IconProps) => (
  <Stroke d="M8 3h8a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2ZM10 18h4" {...p} />
);
export const IconPhoneL = (p: IconProps) => (
  <Stroke d="M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM18 10v4" {...p} />
);
export const IconCube = (p: IconProps) => (
  <Stroke d="M3 7.5 12 3l9 4.5v9L12 21l-9-4.5zM3 7.5 12 12m0 0 9-4.5M12 12v9" {...p} />
);
export const IconSprite = (p: IconProps) => (
  <Stroke d="M3 5h18v14H3zM3 15l5-4 4 3 4-3 5 4M16 9.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Z" {...p} />
);
export const IconGithub = ({ size = 16, ...rest }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ flex: 'none' }} {...rest}>
    <path d="M12 2a10 10 0 0 0-3.16 19.49c.5.09.68-.22.68-.48v-1.7c-2.78.6-3.37-1.34-3.37-1.34-.45-1.16-1.11-1.47-1.11-1.47-.9-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.9 1.52 2.34 1.08 2.91.83.09-.65.35-1.08.63-1.33-2.22-.25-4.55-1.11-4.55-4.94 0-1.09.39-1.99 1.03-2.69-.1-.25-.45-1.27.1-2.65 0 0 .84-.27 2.75 1.02a9.5 9.5 0 0 1 5 0c1.91-1.29 2.75-1.02 2.75-1.02.55 1.38.2 2.4.1 2.65.64.7 1.03 1.6 1.03 2.69 0 3.84-2.33 4.69-4.56 4.93.36.31.68.91.68 1.85v2.74c0 .26.18.58.69.48A10 10 0 0 0 12 2Z" />
  </svg>
);
export const IconGoogle = ({ size = 16, ...rest }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ flex: 'none' }} {...rest}>
    <path fill="#fff" d="M21.6 12.23c0-.74-.07-1.45-.2-2.13H12v4.03h5.4a4.63 4.63 0 0 1-2 3.04v2.52h3.23c1.89-1.74 2.97-4.3 2.97-7.46Z" />
    <path fill="#fff" opacity=".85" d="M12 22c2.7 0 4.96-.9 6.62-2.43l-3.23-2.52c-.9.6-2.04.96-3.39.96-2.6 0-4.81-1.76-5.6-4.12H3.07v2.6A10 10 0 0 0 12 22Z" />
    <path fill="#fff" opacity=".55" d="M6.4 13.89A6 6 0 0 1 6.4 10.1V7.5H3.07a10 10 0 0 0 0 9z" />
    <path fill="#fff" opacity=".7" d="M12 5.98c1.47 0 2.78.5 3.81 1.49l2.86-2.86A10 10 0 0 0 3.07 7.5l3.33 2.6C7.19 7.74 9.4 5.98 12 5.98Z" />
  </svg>
);
export const IconYouTube = ({ size = 16, ...rest }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ flex: 'none' }} {...rest}>
    <path d="M23.5 6.2a3 3 0 0 0-2.12-2.14C19.5 3.55 12 3.55 12 3.55s-7.5 0-9.38.51A3 3 0 0 0 .5 6.2C0 8.07 0 12 0 12s0 3.93.5 5.8a3 3 0 0 0 2.12 2.14c1.87.5 9.38.5 9.38.5s7.5 0 9.38-.5A3 3 0 0 0 23.5 17.8C24 15.93 24 12 24 12s0-3.93-.5-5.8ZM9.55 15.57V8.43L15.82 12l-6.27 3.57Z" />
  </svg>
);
export const IconDiscord = ({ size = 16, ...rest }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ flex: 'none' }} {...rest}>
    <path d="M20.32 4.37a19.79 19.79 0 0 0-4.89-1.52.07.07 0 0 0-.08.04c-.21.38-.44.87-.6 1.25a18.27 18.27 0 0 0-5.49 0c-.16-.4-.4-.88-.62-1.25a.08.08 0 0 0-.08-.04A19.74 19.74 0 0 0 3.68 4.37a.07.07 0 0 0-.03.03C.53 9.05-.32 13.58.1 18.06a.08.08 0 0 0 .03.06 19.91 19.91 0 0 0 5.99 3.03.08.08 0 0 0 .09-.03 14.2 14.2 0 0 0 1.22-2 .08.08 0 0 0-.04-.1 13.1 13.1 0 0 1-1.87-.9.08.08 0 0 1-.01-.12 7.5 7.5 0 0 0 .37-.3.07.07 0 0 1 .08-.01c3.93 1.8 8.18 1.8 12.06 0a.07.07 0 0 1 .08.01c.12.1.25.2.37.3a.08.08 0 0 1-.01.12 12.3 12.3 0 0 1-1.87.9.08.08 0 0 0-.04.1c.36.7.77 1.37 1.22 2a.08.08 0 0 0 .09.03 19.85 19.85 0 0 0 6-3.03.08.08 0 0 0 .03-.05c.5-5.18-.84-9.68-3.55-13.66a.06.06 0 0 0-.03-.03ZM8.02 15.33c-1.18 0-2.16-1.08-2.16-2.42 0-1.33.96-2.42 2.16-2.42 1.21 0 2.18 1.1 2.16 2.42 0 1.34-.96 2.42-2.16 2.42Zm7.97 0c-1.18 0-2.15-1.08-2.15-2.42 0-1.33.95-2.42 2.15-2.42 1.21 0 2.18 1.1 2.16 2.42 0 1.34-.95 2.42-2.16 2.42Z" />
  </svg>
);
export const IconLinkedIn = ({ size = 16, ...rest }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ flex: 'none' }} {...rest}>
    <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29ZM5.34 7.43c-1.15 0-2.07-.93-2.07-2.06 0-1.14.92-2.07 2.07-2.07 1.14 0 2.06.93 2.06 2.07 0 1.13-.92 2.06-2.06 2.06Zm1.78 13.02H3.55V9h3.57v11.45ZM22.22 0H1.77C.8 0 0 .77 0 1.73v20.54C0 23.23.8 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.73C24 .77 23.2 0 22.22 0Z" />
  </svg>
);
export const IconMail = (p: IconProps) => (
  <Custom {...p}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3 7 9 6 9-6" />
  </Custom>
);
export const IconTrash = (p: IconProps) => (
  <Stroke d="M5 7h14M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M10 11v6M14 11v6" {...p} />
);
export const IconMore = ({ size = 16, ...rest }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ flex: 'none' }} {...rest}>
    <circle cx="5" cy="12" r="1.5" />
    <circle cx="12" cy="12" r="1.5" />
    <circle cx="19" cy="12" r="1.5" />
  </svg>
);
export const IconClock = (p: IconProps) => (
  <Custom {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </Custom>
);
export const IconInfo = (p: IconProps) => (
  <Custom {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5M12 8h.01" />
  </Custom>
);
export const IconUpgrade = (p: IconProps) => (
  <Custom {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 16V8M8.5 11.5 12 8l3.5 3.5" />
  </Custom>
);
export const IconServer = (p: IconProps) => (
  <Custom {...p}>
    <rect x="4" y="4" width="16" height="7" rx="1.5" />
    <rect x="4" y="13" width="16" height="7" rx="1.5" />
    <path d="M8 7.5h.01M8 16.5h.01" />
  </Custom>
);
export const IconUser = (p: IconProps) => <Stroke d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM4 21a8 8 0 0 1 16 0" {...p} />;
export const IconSignout = (p: IconProps) => <Stroke d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3M10 17l5-5-5-5M15 12H3" {...p} />;
export const IconDownload = (p: IconProps) => <Stroke d="M12 4v12M6 12l6 6 6-6M4 20h16" {...p} />;
export const IconExternal = (p: IconProps) => <Stroke d="M10 4H4v16h16v-6M14 4h6v6M10 14 20 4" {...p} />;
export const IconImage = (p: IconProps) => <Stroke d="M4 5h16v14H4zM4 16l4.5-4.5 4 4M13 13l3-3 4 4M9.5 9a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z" {...p} />;
export const IconMusic = (p: IconProps) => (
  <Stroke d="M9 18V5l12-2v13M9 18a3 3 0 1 1-6 0 3 3 0 0 1 6 0ZM21 16a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" {...p} />
);
export const IconSpeaker = (p: IconProps) => (
  <Stroke d="M11 5 6 9H2v6h4l5 4zM15.5 8.5a5 5 0 0 1 0 7M19 5a10 10 0 0 1 0 14" {...p} />
);
export const IconStar = ({ size = 14, ...rest }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ flex: 'none' }} {...rest}>
    <path d="M12 2.5l2.95 6.5 7.05.7-5.3 4.84 1.55 6.96L12 17.9 5.75 21.5l1.55-6.96L2 9.7l7.05-.7L12 2.5Z" />
  </svg>
);
export const IconWindows = (p: IconProps) => (
  <Custom {...p}>
    <path d="M3 5.5 11 4.4v7.1H3V5.5Z" />
    <path d="M11 12.5v7.1L3 18.5v-6h8Z" />
    <path d="M21 3.5 12 5v7.5h9V3.5Z" />
    <path d="M12 12.5v7L21 21v-8.5h-9Z" />
  </Custom>
);
export const IconApple = ({ size = 16, ...rest }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ flex: 'none' }} {...rest}>
    <path d="M16.36 12.83c0-2.34 1.94-3.47 2.03-3.52-1.11-1.62-2.83-1.84-3.43-1.86-1.46-.15-2.85.86-3.59.86-.74 0-1.88-.84-3.09-.82-1.59.02-3.06.93-3.88 2.36-1.65 2.86-.42 7.1 1.19 9.42.79 1.13 1.72 2.4 2.93 2.36 1.18-.05 1.62-.76 3.04-.76s1.83.76 3.08.74c1.27-.02 2.08-1.15 2.86-2.29.9-1.32 1.27-2.6 1.29-2.67-.03-.01-2.46-.94-2.49-3.74ZM14.04 5.42c.65-.79 1.09-1.88.97-2.97-.94.04-2.08.62-2.75 1.41-.6.7-1.13 1.81-.99 2.88 1.05.08 2.13-.53 2.77-1.32Z" />
  </svg>
);
export const IconAndroid = (p: IconProps) => (
  <Custom {...p}>
    <path d="M5 11v6a1 1 0 0 0 1 1h2v3M16 18h2a1 1 0 0 0 1-1v-6M5 11a7 7 0 0 1 14 0M9 8 7 5M15 8l2-3M9 14h.01M15 14h.01" />
    <path d="M8 21v-3M16 21v-3" />
  </Custom>
);
export const IconWeb = (p: IconProps) => (
  <Custom {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
  </Custom>
);

/* Brand mark — uses the existing project logo, not the prototype's coral square */
export const IconMark = ({ size = 22 }: { size?: number }) => {
  return (
    <img
      src="/logo.png"
      alt="Entire"
      width={size}
      height={size}
      style={{ width: size, height: size, objectFit: 'contain', display: 'block' }}
    />
  );
};
